import { defineStore } from 'pinia'
import positionApi from '~/apis/positionApi'
import {
  type Position,
  PositionOpenStatus,
  PositionType,
  PositionResult,
  type CreatePositionRequest,
} from '~/constants/positionTypes'
import { exampleAIEngineerPosition } from '~/constants/examplePositions'
import debounce from 'lodash.debounce'
import { getRandomName } from '~/utils/auditUtils'
import { FormStatus } from '~/constants/formTypes'
import jobDescriptionApi from '~/apis/jobDescriptionApi'
import linkedInApi from '~/apis/linkedinApi'
import { useToastStore } from '~/stores/toastStore'
import { ToastType } from '~/constants/toastTypes'
import { apiErrorMessage } from '~/utils/apiUtils'
import {
  createImpactModelSnapshotV1,
  validateImpactModelInputsV1,
  type ImpactModelInputsV1,
  type ImpactModelStageV1,
} from '~/utils/impactModelV1'

const selectedDefault: Position = {
  orgId: '',
  id: '',
  positionName: '',
  positionType: PositionType.FullTime,
  positionData: {},
  status: FormStatus.InProgress,
  openStatus: PositionOpenStatus.Open,
  result: PositionResult.Unfilled,
  createdAt: '',
  updatedAt: '',
}

const debouncedSave = debounce(
  async (id: string, selectedPosition: Position): Promise<void> => {
    const saved = await positionApi.updatePosition(id, selectedPosition)
    const store = usePositionStore()
    if (
      store.selected.id === id &&
      saved?.completedAt &&
      store.selected.completedAt !== saved.completedAt
    )
      store.selected.completedAt = saved.completedAt
  },
  1000,
  { maxWait: 5000 },
)
export const usePositionStore = defineStore('position', {
  state: (): {
    selected: Position
    list: Position[]
  } => ({
    selected: structuredClone(selectedDefault),
    list: [],
  }),
  getters: {},
  actions: {
    async save(flush?: boolean): Promise<void> {
      const { memberPermissions } = usePermissions()
      const p = memberPermissions.value.intakes
      const ownDraft =
        p.create &&
        this.selected.createdBy === useUserStore().user?.id &&
        !this.selected.completedAt
      if (!p.view || (!p.edit && !ownDraft)) {
        debouncedSave.cancel()
        if (flush)
          throw new Error(
            'Editing permission is required to change this content.',
          )
        return
      }
      if (this.$state.selected.id) {
        // Don't save example positions
        if (this.$state.selected.id !== 'example-ai-engineer') {
          this.refreshImpactSnapshot()
          await debouncedSave(this.$state.selected.id, this.$state.selected)
          if (flush) await debouncedSave.flush()
        }
      }
    },
    async updatePositionList(): Promise<void> {
      if (!usePermissions().canViewIntakes.value) {
        debouncedSave.cancel()
        this.list = []
        this.selected = structuredClone(selectedDefault)
        return
      }
      const positions = await positionApi.getPositionList()
      // Add the example position to the list
      this.$state.list = [exampleAIEngineerPosition, ...positions]
    },
    clearSelected() {
      // this.$state.selected = structuredClone(selectedDefault)
    },
    async createNewPosition(
      orgId?: string,
      positionType: PositionType = PositionType.FullTime,
      newPositionObject?: CreatePositionRequest,
    ): Promise<any> {
      const orgStore = useOrgStore()
      const positionName = `Position - ${getRandomName()}`
      const newPosition: CreatePositionRequest = {
        positionName,
        positionType,
        positionData: {},
        orgId: orgId ?? orgStore.selected.id,
        ...newPositionObject,
      }

      const position = await positionApi.createPosition(newPosition)
      await this.updatePositionList()
      this.$patch((state) => {
        state.selected = {
          ...structuredClone(selectedDefault),
          ...position,
          positionData: {
            ...structuredClone(selectedDefault.positionData),
            ...position.positionData,
          },
        }
      })
      await this.updatePositionList()
      return position
    },
    async copyPosition(positionId: string): Promise<any> {
      let position

      // Handle example position copy
      if (positionId === 'example-ai-engineer') {
        position = structuredClone(exampleAIEngineerPosition)
      } else {
        position = await positionApi.getPosition(positionId)
      }

      const newPosition: CreatePositionRequest = {
        orgId: position.orgId,
        positionName: `Copy of ${position.positionName}`,
        positionType: position.positionType,
        positionData: {
          ...structuredClone(selectedDefault.positionData),
          ...position.positionData,
        },
      }
      await this.createNewPosition(
        position.orgId,
        position.positionType,
        newPosition,
      )
    },
    async getPosition(positionId: string): Promise<any> {
      // Check if this is the example position
      if (positionId === 'example-ai-engineer') {
        const clearedPosition = structuredClone(exampleAIEngineerPosition)
        this.$patch((state) => {
          state.selected = {
            ...structuredClone(selectedDefault),
            ...clearedPosition,
            positionData: {
              ...structuredClone(selectedDefault.positionData),
              ...clearedPosition.positionData,
            },
          }
        })
        return clearedPosition
      }

      const position = await positionApi.getPosition(positionId)
      const clearedPosition = position // checkAndClearFields(position)
      this.$patch((state) => {
        state.selected = {
          ...structuredClone(selectedDefault),
          ...clearedPosition,
          positionData: {
            ...structuredClone(selectedDefault.positionData),
            ...clearedPosition.positionData,
          },
        }
      })

      return clearedPosition
    },
    updatePositionField(name: string, value: unknown): void {
      this.selected.positionData[name] = value
      if (name === 'requiredSkills' || name === 'niceToHaveSkills') {
        this.selected.positionData.linkedInBooleanLogic = null
      }
    },
    updateStatus(positionStatus: FormStatus) {
      this.$state.selected.status = positionStatus
    },
    refreshImpactSnapshot(): void {
      const position = this.$state.selected
      if (position.status !== FormStatus.Complete) return

      const stages = position.positionData?.interviewPlan?.stages as
        | ImpactModelStageV1[]
        | undefined
      const inputs = {
        ...(position.positionData?.impactModelInputs ?? {}),
        decisionMethod: position.positionData?.roleClosingMethod,
      } as ImpactModelInputsV1
      if (!stages?.length || validateImpactModelInputsV1(inputs).length > 0)
        return

      const previous = position.positionData?.impactModelSnapshot
      const inputsChanged =
        JSON.stringify(previous?.inputs) !== JSON.stringify(inputs)
      const stagesChanged =
        JSON.stringify(previous?.stages) !== JSON.stringify(stages)
      if (
        !previous ||
        previous.modelVersion !== 'v1' ||
        inputsChanged ||
        stagesChanged
      ) {
        position.positionData.impactModelSnapshot = createImpactModelSnapshotV1(
          inputs,
          stages,
        )
      }
    },

    async generateJobDescription(positionId: string): Promise<void> {
      if (!positionId) return

      try {
        const jobDescription =
          await jobDescriptionApi.generateJobDescription(positionId)
        this.$state.selected.positionData.jobDescription = jobDescription
      } catch (error) {
        console.error('Error regenerating job description:', error)
        const toastStore = useToastStore()
        toastStore.show(
          apiErrorMessage(
            error,
            'Failed to generate job description. Please try again.',
          ),
          ToastType.Error,
        )
        throw error
      }
    },
    async generateLinkedInBooleanLogic(id: string) {
      const position =
        (this.selected?.id === id
          ? this.selected
          : this.list.find((p) => p.id === id)) || this.selected
      if (!position) return

      try {
        const positionData = position.positionData
        const requiredSkills = Array.isArray(positionData?.requiredSkills)
          ? positionData.requiredSkills
          : (positionData?.requiredSkills ?? '')
              .split(',')
              .map((s: string) => s.trim())
        const optionalSkills = Array.isArray(positionData?.niceToHaveSkills)
          ? positionData.niceToHaveSkills
          : (positionData?.niceToHaveSkills ?? '')
              .split(',')
              .map((s: string) => s.trim())

        console.log('LinkedIn payload:', {
          positionId: position.id,
          requiredSkills,
          optionalSkills,
        })

        const linkedInBooleanLogic = await linkedInApi.generateBooleanSearch({
          positionId: position.id,
          requiredSkills,
          optionalSkills,
        })

        await this.updatePositionField(
          'linkedInBooleanLogic',
          linkedInBooleanLogic,
        )
        await this.save()
        return linkedInBooleanLogic
      } catch (error) {
        console.error('Error generating LinkedIn Boolean Logic:', error)
        throw error
      }
    },
    async deletePosition(positionId: string): Promise<void> {
      try {
        // Don't make API call for example positions
        if (positionId !== 'example-ai-engineer') {
          await positionApi.deletePosition(positionId)
        }
        await this.updatePositionList()
      } catch (error) {
        console.error('Error deleting position:', error)
        const toastStore = useToastStore()
        toastStore.show(
          apiErrorMessage(
            error,
            'Failed to delete position. Please try again.',
          ),
          ToastType.Error,
        )
        throw error
      }
    },
  },
})
